import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { sampleAccounts } from './api';
import { z } from '@customerio/hydra-validation';
import { authStore } from '@/services/auth/store';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { useStore } from '@nanostores/react';
import { setSampleWorkspaceId } from '@/services/api';

/**
 * Sample Workspace
 * interactive_demo_workspace / sample_workspace
 * Part of:
 * https://linear.app/customerio/project/sample-workspace-view-only-workspace-387ba35103c0/overview
 */

const useSampleAccountsQuery = () => {
  return useQuery({
    queryKey: ['sampleAccounts'],
    queryFn: getSampleAccounts,
  });
};

const SampleAccountsResponse = z.object({
  accounts: z.array(z.object({ id: z.number(), environment_ids: z.array(z.number()) })),
});

export const getSampleAccounts = async () => {
  // `sampleAccounts` may return undefined or an empty object from cache.
  const parsed = SampleAccountsResponse.safeParse(await sampleAccounts());
  const account = parsed.success ? parsed.data.accounts[0] : undefined;

  const workspaceId = account?.environment_ids[0];
  // Requests scoped to the sample workspace go to the US data center, where it lives.
  setSampleWorkspaceId(workspaceId);
  return { accountId: account?.id ?? 0, workspaceId };
};

export const useSampleWorkspace = () => {
  const auth = useStore(authStore);
  const isBuilder = isBuilderPlan(auth.account);
  const { data: sampleAccount, isLoading, isError } = useSampleAccountsQuery();
  const sampleAccountId = isBuilder ? null : (sampleAccount?.accountId ?? null);
  const sampleWorkspaceId = isBuilder ? null : (sampleAccount?.workspaceId ?? null);

  const isSampleWorkspace = useCallback(
    (workspaceId: number | null | undefined) => {
      if (workspaceId == null) {
        return false;
      }

      return workspaceId === sampleWorkspaceId;
    },
    [sampleWorkspaceId],
  );

  return {
    sampleAccountId,
    sampleWorkspaceId,
    isSampleWorkspace,
    /**
     * While true, `isSampleWorkspace` answers false for everything — hold,
     * don't act on it. Builder plans have no sample workspace, so never load.
     */
    isLoading: isBuilder ? false : isLoading,
    /**
     * A failed sample-accounts read also leaves `isSampleWorkspace` answering
     * false for everything. Guards that must not fail open treat this like
     * the loading case: sampleness is unknown, don't act on it.
     */
    isError: isBuilder ? false : isError,
  };
};
